import React from 'react';

import { ProvenanceDot, ProvenanceState } from '../shared/ProvenanceDot';
import { DistanceConfidence, DistanceProvenance } from '../../types/bag.types';

export interface ProvenanceChipProps {
  provenance: DistanceProvenance;
  confidence?: DistanceConfidence | null;
  showLabel?: boolean;
}

/**
 * My Bag's three provenance values sit on top of the product's existing mark
 * vocabulary (UI_DESIGN_PLAN.md §1.4) rather than inventing a second one:
 * `estimated` is the "derived" ring — computed from things a launch monitor
 * saw — and `entered` is the steel "self-reported" fill.
 */
const DOT_STATE: Record<DistanceProvenance, ProvenanceState> = {
  measured: 'measured',
  estimated: 'derived',
  entered: 'self-reported',
};

const LABEL: Record<DistanceProvenance, string> = {
  measured: 'measured',
  estimated: 'estimated',
  entered: 'entered',
};

const TITLE: Record<DistanceProvenance, string> = {
  measured: 'Measured — a launch monitor saw it',
  estimated: "Estimated — we worked it out from your profile. It can't drive a bend until you confirm it.",
  entered: 'Entered — you told us',
};

/** Cyan is the machine's voice; steel is the golfer's. Green stays out of this
 *  vocabulary entirely so measurement and outcome never blur (§1.4). */
const SHELL: Record<DistanceProvenance, string> = {
  measured: 'bg-cyan/15 text-cyan-ink border-cyan/40',
  estimated: 'bg-transparent text-cyan-ink border-cyan/40',
  entered: 'bg-line text-ink-2 border-line-2',
};

const ROUGHLY_TITLE = "tapped 'roughly' — this rung gets 2 more yards of tolerance";

export default function ProvenanceChip({
  provenance,
  confidence,
  showLabel = true,
}: ProvenanceChipProps): JSX.Element {
  const isRoughly = confidence === 'roughly';
  const title = isRoughly ? `${TITLE[provenance]} · ${ROUGHLY_TITLE}` : TITLE[provenance];

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-brand-sm border px-1.5 py-0.5 text-xs leading-none ${SHELL[provenance]}`}
      title={title}
    >
      <ProvenanceDot state={DOT_STATE[provenance]} />
      {showLabel && <span>{LABEL[provenance]}</span>}
      {isRoughly && (
        <span className="font-mono text-ink-3" aria-label={ROUGHLY_TITLE}>
          ~
        </span>
      )}
      {!showLabel && <span className="sr-only">{LABEL[provenance]}</span>}
    </span>
  );
}

/**
 * The fourth §1.4 state — hollow, "we don't have it". It is not a
 * `DistanceProvenance` value (the wire type has no such member), so it is a
 * sibling rather than a fourth branch of the chip above.
 */
export function EmptyProvenanceChip({ label = 'no distance' }: { label?: string }): JSX.Element {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-brand-sm border border-line-2 bg-transparent px-1.5 py-0.5 text-xs leading-none text-ink-3"
      title="We don't have this one yet"
    >
      <ProvenanceDot state="missing" />
      <span>{label}</span>
    </span>
  );
}

const LEGEND: DistanceProvenance[] = ['measured', 'entered', 'estimated'];

export function ProvenanceChipLegend({ className = '' }: { className?: string }): JSX.Element {
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {LEGEND.map((p) => (
        <ProvenanceChip key={p} provenance={p} />
      ))}
      <EmptyProvenanceChip />
    </div>
  );
}
